import React from 'react';
import { isNil } from 'lodash';

import { useCms } from '~/components/hooks/useCms';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type { AiConfigurationResponse } from '~/server_shared/openapi/data-contracts';
import { reportErrorInProductionOrThrow } from '~/Utils';

import type { SwitchColumns } from './CliveConfigurationTable';

// The Clive configuration record's switch-column union, re-exported from the sysconfig table that
// renders the switches. It is not the full set of config booleans - clive_document_upload, for one,
// is a config value with no switch - so this names the keys the table can render, not every key the
// response carries.
export type CliveConfigBooleanKey = SwitchColumns;

interface CliveConfigurationOptionsValue {
  isLoading: boolean;
  showCliveConfigOptions: boolean;
  isCliveEnabled: (subOrganizationId?: number | null) => boolean;
  isCliveEnabledForOrganization: () => boolean;
  isCliveConfigEnabled: (configKey: CliveConfigBooleanKey, subOrganizationId?: number | null) => boolean;
  isCliveConfigEnabledForOrganization: (configKey: CliveConfigBooleanKey) => boolean;
  isGeneralEmailCliveRecommendationEnabled: () => boolean;
}

interface Organization {
  id: number;
  sub_organizations_enabled?: boolean;
  is_super_organization?: boolean;
}

const CliveConfigurationOptionsContext = React.createContext<CliveConfigurationOptionsValue>({
  isLoading: false,
  showCliveConfigOptions: false,
  isCliveEnabled: () => false,
  isCliveEnabledForOrganization: () => false,
  isCliveConfigEnabled: () => false,
  isCliveConfigEnabledForOrganization: () => false,
  isGeneralEmailCliveRecommendationEnabled: () => false,
});

export const CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY = 'clive_additional_instructions';
export const CLIVE_EXCLUDED_FIELD_KEY = 'clive_excluded';

interface CliveConfigurationOptionsContextProviderProps {
  children: React.ReactNode;
  showInternalCliveConfigOptions?: boolean;
}

export const CliveConfigurationOptionsContextProvider: React.FC<CliveConfigurationOptionsContextProviderProps> = ({
  children,
  showInternalCliveConfigOptions = false,
}) => {
  // Try to get organization from both contexts - use whichever is available
  const sysconfigContext = useSysconfig();
  const cmsContext = useCms();

  // Extract sysconfigContext organization with proper type checking
  const sysconfigOrg = (sysconfigContext as { organization?: Organization })?.organization || null;

  // Determine which organization to use
  const organization: Organization | null = sysconfigOrg || cmsContext?.userOrganization || null;

  // Choose the appropriate API endpoint based on context
  const aiConfigUrl = React.useMemo(() => {
    if (!organization?.id) return null;

    if (sysconfigOrg) {
      // Use sysconfig API for system configuration screens
      return `/sysconfig/api/v1/organizations/${organization.id}/service_ai/configuration`;
    } else {
      // Use regular API for non-sysconfig contexts - avoid namespace conflicts
      return `/api/v1/organizations/${organization.id}/ai_service/configuration`;
    }
  }, [organization?.id, sysconfigOrg]);

  // Fetch AI configuration for the organization
  const { data: aiConfig, isLoading } = useDataFetcher<AiConfigurationResponse>(aiConfigUrl, undefined, {
    shouldFetch: !!organization?.id && !organization?.is_super_organization,
  });

  // Fetch general email Clive status - always use regular API (works in both contexts)
  const generalEmailCliveUrl = React.useMemo(() => {
    if (!organization?.id) return null;
    // Always use regular API - sysconfig users have sufficient permissions
    return `/api/v1/organizations/${organization.id}/general_email_clive_enabled`;
  }, [organization?.id]);

  const { data: generalEmailCliveStatus } = useDataFetcher<{
    is_general_email_clive_enabled?: boolean;
  }>(
    generalEmailCliveUrl,
    {},
    {
      shouldFetch: !!organization?.id,
    }
  );

  // The single place resolving (organizationId, subOrganizationId) -> configuration record.
  const findConfigRecord = React.useCallback(
    (subOrganizationId?: number | null) => {
      // Return null if still loading or no organization
      if (isLoading || !organization?.id) {
        return null;
      }

      if (!aiConfig?.configurations || aiConfig.configurations.length === 0) {
        return null;
      }

      // At this point, organization is guaranteed to exist (passed the early return)
      const org = organization as Organization;

      // If organization has sub-organizations enabled but no subOrganizationId provided, throw error.
      // A null subOrganizationId is a legitimate answer (a claim with no policy resolves to one), so
      // only an omitted argument is reported.
      if (org.sub_organizations_enabled && subOrganizationId === undefined) {
        const error = new Error('subOrganizationId is required for organizations with sub-organizations enabled');
        reportErrorInProductionOrThrow(error);
        return null;
      }

      // If subOrganizationId is provided, look for that specific sub-org config. Tested for nil
      // rather than truthiness so an explicit 0 counts as a sub-org id instead of falling through
      // to the org-level lookup below; null and an omitted argument both resolve org-level.
      if (!isNil(subOrganizationId)) {
        return (
          aiConfig.configurations.find(
            (c) => c.organization_id === org.id && c.sub_organization_id === subOrganizationId
          ) ?? null
        );
      }

      // For organizations without sub-orgs, look for organization-level config
      return (
        aiConfig.configurations.find((c) => c.organization_id === org.id && c.sub_organization_id === null) ?? null
      );
    },
    [aiConfig, isLoading, organization]
  );

  const isCliveConfigEnabled = React.useCallback(
    (configKey: CliveConfigBooleanKey, subOrganizationId?: number | null): boolean =>
      findConfigRecord(subOrganizationId)?.[configKey] === true,
    [findConfigRecord]
  );

  const isCliveConfigEnabledForOrganization = React.useCallback(
    (configKey: CliveConfigBooleanKey): boolean => {
      // An organization-level record answers on its own. Resolved through findConfigRecord with an
      // explicit null so the two resolvers cannot disagree; null also keeps the sub-organization
      // argument guard inside it from firing.
      const orgConfig = findConfigRecord(null);
      if (orgConfig) {
        return orgConfig[configKey] === true;
      }

      // findConfigRecord also answers null while loading or without an organization, where the
      // sub-organization fallback below must not run against a stale or absent response.
      if (isLoading || !organization?.id) {
        return false;
      }

      // No organization-level record: an organization with sub-organizations counts as enabled when
      // ANY of its sub-organization records has the key on.
      return (aiConfig?.configurations ?? []).some(
        (c) => c.organization_id === organization.id && c.sub_organization_id !== null && c[configKey] === true
      );
    },
    [findConfigRecord, aiConfig, isLoading, organization]
  );

  const isCliveEnabled = React.useCallback(
    (subOrganizationId?: number | null): boolean => isCliveConfigEnabled('send_ai_data', subOrganizationId),
    [isCliveConfigEnabled]
  );

  const isCliveEnabledForOrganization = React.useCallback(
    (): boolean => isCliveConfigEnabledForOrganization('send_ai_data'),
    [isCliveConfigEnabledForOrganization]
  );

  const isGeneralEmailCliveRecommendationEnabled = React.useCallback((): boolean => {
    // Show Clive analysis column if:
    // 1. Organization has Clive enabled (prerequisite)
    // 2. AND "Enable Clive Recommendation" is turned on for general email
    return isCliveEnabledForOrganization() && generalEmailCliveStatus?.is_general_email_clive_enabled === true;
  }, [isCliveEnabledForOrganization, generalEmailCliveStatus]);

  // Computed value to determine if Clive config options should be shown
  // For internal sysconfig (super-org): always show
  // For external sysconfig: show only if Clive is enabled for organization
  const showCliveConfigOptions = React.useMemo(() => {
    // Always show for internal config (super-org)
    if (showInternalCliveConfigOptions) {
      return true;
    }

    if (!organization) return false;

    return isCliveEnabledForOrganization();
  }, [showInternalCliveConfigOptions, organization, isCliveEnabledForOrganization]);

  return (
    <CliveConfigurationOptionsContext.Provider
      value={{
        isLoading,
        showCliveConfigOptions,
        isCliveEnabled,
        isCliveEnabledForOrganization,
        isCliveConfigEnabled,
        isCliveConfigEnabledForOrganization,
        isGeneralEmailCliveRecommendationEnabled,
      }}
    >
      {children}
    </CliveConfigurationOptionsContext.Provider>
  );
};

export { CliveConfigurationOptionsContext };

export const useCliveConfigOptions = (): CliveConfigurationOptionsValue => {
  return React.useContext(CliveConfigurationOptionsContext);
};
